
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import axiosInstance from "../api/axiosInstance";

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (
        !storedUser ||
        storedUser === "undefined" ||
        storedUser === "null"
      ) {
        return null;
      }

      return JSON.parse(storedUser);
    } catch (error) {
      console.error("Error parsing stored user:", error);
      localStorage.removeItem("user");
      return null;
    }
  });

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);

  // Financial year state
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] =
    useState(null);
  const [financialYearLoading, setFinancialYearLoading] =
    useState(false);

  const clientId = user?.client_id || company?.client_id || null;

  // Fetch company details
  useEffect(() => {
    let cancelled = false;

    const fetchCompanyData = async () => {
      setLoading(true);

      try {
        if (!user?.client_id) {
          if (!cancelled) setCompany(null);
          return;
        }

        const response = await axiosInstance.get(
          `/api/clients/${user.client_id}`
        );

        if (!cancelled) {
          setCompany(response.data?.client || response.data || null);
        }
      } catch (error) {
        console.error("Error fetching company data:", error);

        if (!cancelled) setCompany(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCompanyData();

    return () => {
      cancelled = true;
    };
  }, [user, reload]);

  // Fetch financial years for the logged-in client
  useEffect(() => {
    let cancelled = false;

    const fetchFinancialYears = async () => {
      setFinancialYears([]);
      setSelectedFinancialYear(null);

      if (!clientId) {
        setFinancialYearLoading(false);
        return;
      }

      setFinancialYearLoading(true);

      try {
        const response = await axiosInstance.get(
          "/api/financial-years"
        );

        const years = Array.isArray(response.data?.financialYears)
          ? response.data.financialYears
          : [];

        if (cancelled) return;

        setFinancialYears(years);

        if (years.length === 0) return;

        const storageKey = `selectedFinancialYear_${clientId}`;
        const savedFyId = localStorage.getItem(storageKey);

        let selectedYear = years.find(
          (year) => String(year.fyid) === String(savedFyId)
        );

        // Default to the financial year containing today's date.
        if (!selectedYear) {
          const today = new Date();

          selectedYear = years.find((year) => {
            const start = new Date(year.start_date);
            const end = new Date(year.end_date);

            return today >= start && today <= end;
          });
        }

        // If unavailable, select the latest year returned by the API.
        if (!selectedYear) {
          selectedYear = [...years].sort(
            (a, b) =>
              new Date(b.start_date) - new Date(a.start_date)
          )[0];
        }

        setSelectedFinancialYear(selectedYear);

        localStorage.setItem(
          storageKey,
          String(selectedYear.fyid)
        );
      } catch (error) {
        console.error("Error fetching financial years:", error);

        if (!cancelled) {
          setFinancialYears([]);
          setSelectedFinancialYear(null);
        }
      } finally {
        if (!cancelled) setFinancialYearLoading(false);
      }
    };

    fetchFinancialYears();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Select another financial year from the header
  const selectFinancialYear = useCallback(
    (fyid) => {
      const selectedYear = financialYears.find(
        (year) => String(year.fyid) === String(fyid)
      );

      if (!selectedYear || !clientId) return;

      setSelectedFinancialYear(selectedYear);

      localStorage.setItem(
        `selectedFinancialYear_${clientId}`,
        String(selectedYear.fyid)
      );
    },
    [financialYears, clientId]
  );

  // Preserve existing setUser behaviour
  const updateUser = (userData) => {
    setUser(userData);

    if (userData) {
      localStorage.setItem("user", JSON.stringify(userData));
    } else {
      localStorage.removeItem("user");
      setCompany(null);
      setFinancialYears([]);
      setSelectedFinancialYear(null);
    }
  };

  return (
    <AppContext.Provider
      value={{
        user,
        setUser: updateUser,
        company,
        setCompany,
        clientId,
        loading,
        reload,
        setReload,

        financialYears,
        selectedFinancialYear,
        fyid: selectedFinancialYear?.fyid ?? null,
        selectFinancialYear,
        financialYearLoading,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
